import React, { lazy, useEffect, useState } from "react";
import "./App.css";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  selectCurrentToken,
  setCredentials,
  clearCredentials,
} from "./app/authSlice";
import checkAuth from "./app/auth";
import initializeApp from "./app/init";

/**
 * PersistLogin — wraps protected areas and attempts a silent token refresh
 * when the Redux token is absent (e.g. after a page reload).
 * The httpOnly refresh-token cookie is sent automatically by the browser.
 */
const PersistLogin = ({ children }) => {
  const token = useSelector(selectCurrentToken);
  const dispatch = useDispatch();
  const [isVerifying, setIsVerifying] = useState(!token);

  useEffect(() => {
    if (token) {
      setIsVerifying(false);
      return;
    }

    let cancelled = false;
    const silentRefresh = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}api/v1/en/admin/refresh-token`,
          { method: "POST", credentials: "include" },
        );
        if (!cancelled && res.ok) {
          const body = await res.json();
          if (body?.data?.success === 1) {
            dispatch(
              setCredentials({
                token: body.data.data.token,
                maxSessionUntil: body.data.data.maxSessionUntil,
              }),
            );
          } else {
            dispatch(clearCredentials());
          }
        } else if (!cancelled) {
          dispatch(clearCredentials());
        }
      } catch {
        if (!cancelled) dispatch(clearCredentials());
      } finally {
        if (!cancelled) setIsVerifying(false);
      }
    };

    silentRefresh();
    return () => {
      cancelled = true;
    };
  }, [token, dispatch]);

  if (isVerifying) {
    // Minimal full-screen spinner while we verify the session
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="w-8 h-8 border-4 border-[#9D2235] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return children;
};

/**
 * ProtectedRoute — redirects to /login when there is no access token in
 * Redux state.  Must be nested inside <PersistLogin> so the silent-refresh
 * attempt happens before this guard runs.
 */
const ProtectedRoute = ({ children }) => {
  const token = useSelector(selectCurrentToken);
  return token ? children : <Navigate to="/login" replace />;
};

// Importing pages
const CreateStudent = lazy(
  () => import("./features/students/components/CreateStudent"),
);
const ViewStudent = lazy(
  () => import("./features/students/components/ViewStudent"),
);

const Layout = lazy(() => import("./containers/Layout"));
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Register = lazy(() => import("./pages/Register"));
const CreateListeningTest = lazy(
  () => import("./features/listeingTest/components/CreateListeningTest"),
);

const ViewEmployee = lazy(
  () => import("./features/employeeAcounts/components/ViewEmployee"),
);
// Initializing different libraries
initializeApp();

// Set up axios defaults / interceptors
checkAuth();

function App() {
  useEffect(() => {
    // App initialization
  }, []);

  return (
    <>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/register" element={<Register />} />

          <Route
            path="/app/*"
            element={
              <PersistLogin>
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              </PersistLogin>
            }
          />

          <Route
            path="*"
            element={
              <PersistLogin>
                <ProtectedRoute>
                  <Navigate to="/app/welcome" replace />
                </ProtectedRoute>
              </PersistLogin>
            }
          />
          {/* <Route path="/app/employee/view/:id" element={<ViewEmployee />} /> */}
        </Routes>
      </Router>
    </>
  );
}

export default App;
